import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

/**
 * Who the monthly CBAM Money Market Monitor email is sent to.
 *
 * The list lives in public.mm_notification_recipients as one semicolon delimited string, and
 * mm-new-month-notify reads it at send time, so an edit here changes the next send with no
 * deploy. The card renders only when the row comes back: read is restricted by RLS to the
 * investment team and admins, so anyone else simply does not see it. The visibility is a
 * courtesy, the enforcement is the policy.
 */

// Same shape the edge function validates against, so the count shown here is the count that
// will actually be mailed rather than an optimistic one.
const ADDRESS = /^[^@\s;,]+@[^@\s;,]+\.[^@\s;,]+$/;

export const parseRecipients = (raw: string): string[] => [
  ...new Set(
    String(raw ?? "")
      .split(";")
      .map((s) => s.trim().toLowerCase())
      .filter((s) => ADDRESS.test(s)),
  ),
];

const MmEmailRecipientsCard = () => {
  const { toast } = useToast();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");
  const [value, setValue] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("mm_notification_recipients" as any)
        .select("recipients")
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setVisible(false);
        setLoading(false);
        return;
      }
      const current = (data as any).recipients ?? "";
      setSaved(current);
      setValue(current);
      setVisible(true);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !visible) return null;

  const parsed = parseRecipients(value);
  const dirty = value !== saved;
  // A trailing semicolon or a blank line is normal while typing, so only flag text that
  // contains something which is not going to be mailed.
  const droppedCount =
    value.split(";").filter((s) => s.trim().length > 0).length - parsed.length;

  const handleSave = async () => {
    setSaving(true);
    const { data: userData } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("mm_notification_recipients" as any)
      .update({
        recipients: value,
        updated_at: new Date().toISOString(),
        updated_by: userData?.user?.id ?? null,
      })
      .eq("id", true);
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    setSaved(value);
    toast({
      title: "Recipients saved",
      description: `The next Money Market Monitor email will go to ${parsed.length} address${parsed.length === 1 ? "" : "es"}.`,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Money Market Monitor email recipients
        </CardTitle>
        <CardDescription>
          Separate addresses with a semicolon. This is exactly who the monthly CBAM Money Market
          Monitor email is sent to, from the next send onwards. The job runs at 07:30 each day and
          sends once, when a new month appears in the performance sheet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={4}
          spellCheck={false}
          className="font-mono text-sm"
          placeholder="adviserteams@ascotwm.com; invteam@capeberkshire.co.uk"
          aria-label="Money Market Monitor email recipients, semicolon delimited"
        />

        <div className="flex flex-wrap items-center gap-2">
          {parsed.length === 0 ? (
            <span className="flex items-center gap-1.5 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              No valid address. The job will refuse to send rather than claim the month.
            </span>
          ) : (
            parsed.map((email) => (
              <Badge key={email} variant="secondary" className="font-mono font-normal">
                {email}
              </Badge>
            ))
          )}
        </div>

        {droppedCount > 0 && (
          <p className="flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-500">
            <AlertTriangle className="h-4 w-4" />
            {droppedCount} entr{droppedCount === 1 ? "y is" : "ies are"} not a valid email address
            and will be ignored.
          </p>
        )}

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={!dirty || saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save recipients
          </Button>
          {dirty && !saving && (
            <Button variant="ghost" onClick={() => setValue(saved)}>
              Discard changes
            </Button>
          )}
          {!dirty && !saving && (
            <span className="text-sm text-muted-foreground">
              Saved. {parsed.length} recipient{parsed.length === 1 ? "" : "s"}.
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MmEmailRecipientsCard;
